import { ref, onMounted, onUnmounted, type ComputedRef, type Ref } from 'vue'
import { useAnchoredPosition } from './useAnchoredPosition'

interface TooltipPosition {
  isVisible: Ref<boolean>
  tooltipRef: Ref<HTMLElement | null>
  triggerRef: Ref<HTMLElement | null>
  tooltipStyle: ComputedRef<{ top: string; left: string }>
  showTooltip: () => void
  hideTooltip: () => void
}

/**
 * Visibility and dismissal of a teleported tooltip. Placement — centred on the
 * trigger, below it when there is room and above it otherwise, clamped into the
 * viewport and re-placed while any scroller under it moves — is
 * `useAnchoredPosition`'s `below` axis, so a tooltip and the agenda hover card
 * measure the same things in the same way (#3665).
 *
 * Bind `triggerRef` to the trigger element and `tooltipRef` + `tooltipStyle`
 * to the teleported tooltip element; call `showTooltip`/`hideTooltip` from
 * hover and focus events. Hides on Escape, and when the trigger scrolls out of
 * view under it.
 */
export function useTooltipPosition(): TooltipPosition {
  const isVisible = ref(false)
  const tooltipRef = ref<HTMLElement | null>(null)
  const triggerRef = ref<HTMLElement | null>(null)

  function showTooltip() {
    isVisible.value = true
  }

  function hideTooltip() {
    isVisible.value = false
  }

  const { positionStyle } = useAnchoredPosition({
    anchorEl: () => triggerRef.value,
    floatingEl: tooltipRef,
    visible: () => isVisible.value,
    onAnchorLost: hideTooltip,
    placement: 'below',
  })

  // Close on escape key
  function handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape' && isVisible.value) {
      hideTooltip()
    }
  }

  onMounted(() => {
    document.addEventListener('keydown', handleKeyDown)
  })

  onUnmounted(() => {
    document.removeEventListener('keydown', handleKeyDown)
  })

  return { isVisible, tooltipRef, triggerRef, tooltipStyle: positionStyle, showTooltip, hideTooltip }
}
